import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import "./GeneratePage.scss";
import { message } from "antd";
import dayjsTZ from "../../../utils/dayjs";
import { pickerToDayKey } from "../../../utils/timezone/dayBounds";
import { getCompanyTimezone } from "../../../utils/timezone/teamTimezone";
import GeneratePageHeader from "./GeneratePageHeader";
import ReportContent from "./ReportContent/ReportContent";
import { useSelector, useDispatch } from "react-redux";
import { processFactory } from "../processFactory/processFactory";
import LoadableComp from "./../../commonComponents/LoadableComp/LoadableComp";
import { API } from "../../../helpers/API/amplifyApi";
import { useRedux } from "../../../hooks/useRedux";
import { useReportsData } from "./reportContext/reportHelpers";
import {
  ensureDeferredTables,
  tablesForReport,
} from "../../../helpers/API/ensureDeferredTables";
import { fetchEmployeesForReport } from "./fetchEmployeesForReport";
import { reportsConstants } from "./constants";
import { useAuth } from "../../../authentication/authHelpers";
import {
  getManagedTeam,
  isBranchManager,
  filterEmployeesByTeam,
} from "../../../utils/branchScope";

const DEFAULT_FILTERS_FIELD_ID = "0d452d-1f23tut-4b2d6gt5-a7d8e8w6";

const REPORT_TYPES_LIST = [
  "approvals",
  "supervisorApprovals",
  "clockingTypesReports",
  "openForgottenShifts",
  "missingPunches",
  "plannedVsActual",
  "otBank",
  "hoursCostByDept",
  "requests",
  "leaveBalance",
  "leaveCoverage",
  "bankForwardPay",
  "prepayments",
  "detailedTaxes",
  "employeesList",
  "suspensions",
  "employeesLeave",
];

/** Reports that read employeeRequests (no clocking fetch required). */
const REQUESTS_REPORTS = new Set([
  "requests",
  "leaveBalance",
  "leaveCoverage",
]);

/** Reports that need period-scoped clockings from the API. */
const CLOCKING_REPORTS = new Set([
  "approvals",
  "supervisorApprovals",
  "clockingTypesReports",
  "openForgottenShifts",
  "missingPunches",
  "plannedVsActual",
  "otBank",
  "hoursCostByDept",
  "employeesLeave",
]);

function GeneratePage({}) {
  const { reportType } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useAuth();
  const [reportClockings, setReportClockings] = useRedux(
    "reportClockings",
    [],
    false,
  );
  /** Server-scoped employees for employeesList — not the full Redux dump. */
  const [reportEmployees, setReportEmployees] = useState([]);
  const { changeDrawerFilter } = useReportsData();
  const branchTeam = isBranchManager(auth) ? getManagedTeam(auth) : null;

  const [
    { wages },
    { clockings },
    { isDarkMode },
    { prepayments },
    { programFields },
    { employees },
    { employeeRequests },
  ] = useSelector((state) => [
    state.wages,
    state.clockings,
    state.usersList,
    state.prepayments,
    state.programFields,
    state.employeesList,
    state.employeeRequests,
  ]);

  const [loading, setLoading] = useState(true);
  const [updatingTable, setUpdatingTable] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);
  const [customFilter, setCustomFilter] = useState(null);
  const [manualColumns, setManualColumns] = useState(null);

  // Local reportsConstants are the source of truth for new reports;
  // programFields can override column defs when customized in-app.
  const constants = useMemo(() => {
    const idx = (programFields || []).findIndex(
      (pf) => pf.fieldId === DEFAULT_FILTERS_FIELD_ID,
    );
    const fromPf =
      idx === -1 ? {} : programFields[idx]?.fieldOptions || {};
    return { ...reportsConstants, ...fromPf };
  }, [programFields]);

  // single dispatch: only the active report type is processed
  // memo recomputes purely on referential dependency changes
  const contentData = useMemo(() => {
    if (loading || !REPORT_TYPES_LIST.includes(reportType)) return undefined;
    const def = constants[reportType];

    switch (reportType) {
      case "approvals":
      case "clockingTypesReports":
      case "openForgottenShifts":
      case "missingPunches":
      case "plannedVsActual":
      case "otBank":
      case "hoursCostByDept":
        // source rows are read from the redux store inside the processor
        return processFactory(
          reportType,
          [],
          activeFilter,
          def,
          manualColumns,
          employees,
        );
      case "supervisorApprovals":
        return processFactory(reportType, [], activeFilter, def, manualColumns);
      case "requests":
      case "leaveBalance":
      case "leaveCoverage":
        return processFactory(
          reportType,
          (employeeRequests || []).map((el) => ({
            ...el,
            employeeDetails: employees?.find((e) => e.userSub === el.userSub),
          })),
          activeFilter,
          def,
          manualColumns,
          employees,
        );
      case "bankForwardPay":
      case "detailedTaxes":
        return processFactory(
          reportType,
          (wages || []).map((el) => ({
            ...el,
            employeeDetails: employees?.find(
              (e) => e.employeeId === el.employeeId,
            ),
          })),
          activeFilter,
          def,
          manualColumns,
        );
      case "prepayments":
        return processFactory(
          reportType,
          (prepayments || []).map((el) => ({
            ...el,
            employeeDetails:
              employees?.find((e) => e.employeeId === el.employeeId) || {},
          })),
          activeFilter,
          def,
          manualColumns,
        );
      case "employeesList":
        return processFactory(
          reportType,
          reportEmployees,
          activeFilter,
          def,
          manualColumns,
        );
      case "suspensions":
        return processFactory(
          reportType,
          employees,
          activeFilter,
          def,
          manualColumns,
        );
      case "employeesLeave": {
        const array = (reportClockings?.length ? reportClockings : clockings || []).map(
          (el) => {
            const employee =
              (employees || []).find((e) => e.employeeId === el.employeeId) ||
              {};
            return { ...el, employeeDetails: { ...employee } };
          },
        );
        return processFactory(
          reportType,
          array,
          activeFilter,
          def,
          manualColumns,
        );
      }
      default:
        return undefined;
    }
  }, [
    reportType,
    loading,
    constants,
    activeFilter,
    manualColumns,
    employees,
    reportEmployees,
    clockings,
    wages,
    prepayments,
    employeeRequests,
    reportClockings,
  ]);

  // region RETRIEVE FILTERS
  const filterReportsClockings = async (e) => {
    if (JSON.stringify(e) === JSON.stringify(activeFilter)) return;

    !!customFilter && setCustomFilter(null);
    setUpdatingTable(true);

    // employeesList: query employees with status/employee filters — skip clocking.
    if (reportType === "employeesList") {
      try {
        const list = await fetchEmployeesForReport({
          statuses: e?.employeeStatuses || [],
          employeeId: e?.employeeId || null,
          employeeTeam: branchTeam || null,
        });
        const rows = Array.isArray(list) ? list : [];
        setReportEmployees(
          branchTeam ? filterEmployeesByTeam(rows, branchTeam) : rows,
        );
        setActiveFilter(e);
        if (e?.employeeFullName) {
          changeDrawerFilter((prev = {}) => ({
            ...prev,
            Punonjësi: [e.employeeFullName],
          }));
        }
        message.success({
          key: "employeesList",
          content: "Fetched successfully!",
        });
      } catch (err) {
        console.error(err);
        message.error({
          key: "employeesList",
          content: "Failed fetching employees!",
        });
      } finally {
        setUpdatingTable(false);
      }
      return;
    }

    // Request / leave reports only need the filter + deferred employeeRequests.
    if (REQUESTS_REPORTS.has(reportType) || reportType === "suspensions") {
      setActiveFilter(e);
      if (e?.employeeFullName) {
        changeDrawerFilter((prev = {}) => ({
          ...prev,
          Punonjësi: [e.employeeFullName],
        }));
      }
      message.success({
        key: reportType,
        content: "Fetched successfully!",
      });
      setUpdatingTable(false);
      return;
    }

    if (!CLOCKING_REPORTS.has(reportType)) {
      setActiveFilter(e);
      setUpdatingTable(false);
      return;
    }

    const dayType = reportType === "employeesLeave" ? "HolidayRequest" : false;
    try {
      const r = await getReportsClockings(
        e?.timeInterval,
        e?.employeeId,
        dayType,
      );
      const newClockings = [];
      r.forEach((cl) => {
        const idx = (employees || []).findIndex(
          (el) => el?.employeeId == cl?.employeeId,
        );
        if (idx !== -1) {
          newClockings.push({ ...cl, employeeDetails: employees[idx] });
        } else {
          newClockings.push(cl);
        }
      });

      setActiveFilter(e);
      if (e?.employeeFullName) {
        changeDrawerFilter((prev = {}) => ({
          ...prev,
          Punonjësi: [e.employeeFullName],
        }));
      }
      setReportClockings(
        newClockings.sort((a, b) => b?.clockOutDate - a?.clockOutDate),
      );
      message.success({
        key: "approvals",
        content: "Fetched successfully!",
      });
    } catch (err) {
      console.error(err);
      message.error({
        key: "approvals",
        content: "Failed fetching clockings!",
      });
    } finally {
      setUpdatingTable(false);
    }
  };
  const updateColumns = (cols, callback) => {
    !!callback && callback();
    setManualColumns(cols);
  };
  const onGenerate = (filter) => {
    const source = contentData || { rowData: [], columnsDef: [] };
    const activeFields = Object.entries(filter || {}).filter(
      ([, values]) => Array.isArray(values) && values.length,
    );

    if (!activeFields.length) {
      setCustomFilter(null);
      return;
    }

    const columnsDef = source.columnsDef || [];
    // map each filtered field (a column label) to its column index, so we match
    // the selected values against the right column only (AND across fields).
    const fieldIndexes = activeFields
      .map(([label, values]) => [columnsDef.indexOf(label), values])
      .filter(([idx]) => idx !== -1);

    const arr = {
      ...source,
      rowData: (source.rowData || []).filter((row) =>
        fieldIndexes.every(([idx, values]) => values.includes(row[idx])),
      ),
    };
    setCustomFilter(arr);
  };

  // Load only the tables this report type needs (period-scoped payroll).
  useEffect(() => {
    if (!reportType || !REPORT_TYPES_LIST.includes(reportType)) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const needed = tablesForReport(reportType);
      await ensureDeferredTables(
        dispatch,
        { wages, prepayments, employeeRequests },
        needed,
      );
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when report type changes
  }, [reportType, dispatch]);

  useLayoutEffect(() => {
    if (!employees || !programFields) return;
    const needed = tablesForReport(reportType);
    const ready =
      !!employees &&
      needed.every((t) => {
        if (t === "wages") return !!wages;
        if (t === "prepayments") return !!prepayments;
        if (t === "employeeRequests") return !!employeeRequests;
        return true;
      });
    if (ready) setLoading(false);
  }, [
    reportType,
    employees,
    programFields,
    wages,
    prepayments,
    employeeRequests,
  ]);

  useEffect(() => {
    if (!!reportType && !REPORT_TYPES_LIST.includes(reportType)) {
      setTimeout(() => {
        navigate("/raportet");
      }, 1500);
    }
  }, [reportType]);

  //region RETURN
  return (
    <LoadableComp loading={loading}>
      <div className="globalPageWrapper">
        <div className={`generate-page ${isDarkMode ? "dark" : ""}`}>
          <GeneratePageHeader
            {...{
              filterReportsClockings,
              contentData,
              updateColumns,
              constants,
            }}
            src={reportType}
          />
          <ReportContent
            page={reportType}
            onGenerate={onGenerate}
            infos={constants}
            activeFilter={activeFilter}
            updatingTable={updatingTable}
            resetCustom={() => setCustomFilter(null)}
            unfilteredContentData={contentData}
            contentData={!!customFilter ? customFilter : contentData}
          />
        </div>
      </div>
    </LoadableComp>
  );
}

export default GeneratePage;

// Picker calendar day → company IANA day bounds (never browser-local).
const toLocalDayString = (value) =>
  pickerToDayKey(value, getCompanyTimezone()) ||
  dayjsTZ(value).format("YYYY-MM-DD");

export const getReportsClockings = async (
  timeInterval,
  employeeId = false,
  dayType = false,
) => {
  return new Promise(async (resolve, reject) => {
    let filterClockingObj = {
      limiter: dayjsTZ(toLocalDayString(timeInterval[0]))
        .startOf("day")
        .valueOf(),
      // Tight window: end of selected end-day (+1d buffer for overnight shifts).
      endLimiter: dayjsTZ(toLocalDayString(timeInterval[1]))
        .add(1, "day")
        .endOf("day")
        .valueOf(),
    };
    if (employeeId) {
      filterClockingObj.employeeId = employeeId;
    }
    if (dayType) {
      filterClockingObj.dayType = dayType;
    }
    await API.get(`clocking`, `/clocking`, {
      queryStringParameters: {
        ...filterClockingObj,
      },
    })
      .then((r) => resolve(r))
      .catch((error) => {
        console.error(error);
        reject(error);
      });
  });
};
